import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    Grid,
    Button,
    IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { InfoApi } from "../../../api/InfoApi";
import { LeaderboardApi } from "../../../api/LeaderboardApi";
import RankingLeaderboard from "./RankingLeaderboard";

const MAIN_FONT = "Noto Sans TC";
const DIALOG_TITLE_FONT_SIZE = 20;
const LS_KEY = "last_selected_skill_in_top_performer";

const HEADING = "STEAiM-in-PE 比賽 最項目積分排行榜";



// ── Skill Select Dialog ────────────────────────────────────────────────────────

function SkillSelectDialog({ open, onClose, skillList, selectedSkill, onSelect }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    width: 800,       // fixed px
                    maxWidth: "90vw", // never overflow viewport
                    background: "linear-gradient(135deg, #0D1B3E 0%, #060D1F 100%)",
                    border: "2px solid rgba(130, 60, 255, 0.7)",
                    borderRadius: "16px",
                    boxShadow: "0 0 30px rgba(130, 60, 255, 0.4)",
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontFamily: MAIN_FONT,
                    color: "#00E5FF",
                    fontWeight: "700",
                    borderBottom: "1px solid rgba(130, 60, 255, 0.3)",
                    pr: 6,
                }}
            >
                選擇項目
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        position: "absolute",
                        right: 12,
                        top: 12,
                        color: "rgba(180, 160, 220, 0.7)",
                        "&:hover": { color: "#00E5FF" },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ maxHeight: 400, overflowY: "auto", pt: 2 }}>
                <Grid container spacing={1}>
                    {skillList.map((skill) => {
                        const isActive = selectedSkill?.code === skill.code;
                        return (
                            <Grid item xs={3} key={skill.code}>
                                <Button
                                    fullWidth
                                    onClick={() => onSelect(skill)}
                                    sx={{
                                        fontFamily: MAIN_FONT,
                                        fontSize: DIALOG_TITLE_FONT_SIZE,
                                        fontWeight: isActive ? "700" : "400",
                                        color: isActive ? "#00E5FF" : "rgba(180, 160, 220, 0.8)",
                                        background: isActive
                                            ? "linear-gradient(135deg, rgba(130,60,255,0.35) 0%, rgba(0,229,255,0.1) 100%)"
                                            : "rgba(130, 60, 255, 0.08)",
                                        border: isActive
                                            ? "1px solid rgba(0, 229, 255, 0.6)"
                                            : "1px solid rgba(130, 60, 255, 0.25)",
                                        borderRadius: "8px",
                                        textTransform: "none",
                                        width: "100%",
                                        height: 120,
                                        lineHeight: 1.3,
                                        // truncate overflow text
                                        display: "block",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        // whiteSpace: "nowrap",
                                        px: 1,
                                        "&:hover": {
                                            background: "linear-gradient(135deg, rgba(130,60,255,0.25) 0%, rgba(0,229,255,0.08) 100%)",
                                            color: "#00E5FF",
                                            border: "1px solid rgba(0, 229, 255, 0.4)",
                                        },
                                    }}
                                >
                                    {skill.name_zh || skill.name_en || skill.code}
                                </Button>
                            </Grid>
                        );
                    })}
                </Grid>
            </DialogContent>
        </Dialog>
    );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function TopSkillPlayerLeaderboard({
    orgType = "secondary",
    refreshInterval = -1,
    topRankCount = 3,
}) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [skillList, setSkillList] = useState([]);
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [leaderboardData, setLeaderboardData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [skillListError, setSkillListError] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const orgTypeLabel = orgType === "primary" ? "小學" : orgType === "secondary" ? "中學" : "";

    // ── Reload when orgType changes ───────────────────────────────────────────
    useEffect(() => {
        if (skillList.length > 0 && selectedSkill) {
            loadPerformers(selectedSkill.code, orgType);
        }
    }, [orgType]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Load skill list on mount ──────────────────────────────────────────────
    useEffect(() => {
        loadSkillList();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadSkillList = async () => {
        try {
            const result = await InfoApi.getSteminpeSkillList();
            const list = result || [];
            if (list.length === 0) {
                setSkillListError("未有任何項目清單");
                return;
            }
            setSkillList(list);
            resolveInitialSkill(list);
        } catch {
            setSkillListError("未有任何項目清單");
        }
    };

    const resolveInitialSkill = (list) => {
        const skillParam = searchParams.get("skill");

        let resolved = null;

        if (skillParam) {
            resolved = list.find((s) => s.code === skillParam) ?? null;
        }

        if (!resolved) {
            const lastCode = localStorage.getItem(LS_KEY);
            if (lastCode) {
                resolved = list.find((s) => s.code === lastCode) ?? null;
            }
        }

        if (!resolved) {
            resolved = list[Math.floor(Math.random() * list.length)];
        }

        setSelectedSkill(resolved);
        loadPerformers(resolved.code, orgType);
    };

    // ── Load top performers ───────────────────────────────────────────────────
    const loadPerformers = async (skillCode, currentOrgType = orgType) => {
        setIsLoading(true);
        try {
            const result = await LeaderboardApi.getTopSkillPlayer(skillCode, currentOrgType);
            const transformed = (result || []).map(
                ({ id, display_name, org_name, rank, max_score }) => ({
                    id,
                    itemName: `${display_name} (${org_name})`,
                    rank,
                    score: max_score,
                })
            );
            setLeaderboardData(transformed.slice(0, topRankCount));
        } catch {
            setLeaderboardData([]);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Skill change from dialog ──────────────────────────────────────────────
    const handleSkillChange = (skill) => {
        setSelectedSkill(skill);
        setSearchParams({ skill: skill.code });
        localStorage.setItem(LS_KEY, skill.code);
        setDialogOpen(false);
        loadPerformers(skill.code, orgType);
    };

    // ── Error state ───────────────────────────────────────────────────────────
    if (skillListError) {
        return (
            <Box
                sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <Typography
                    sx={{
                        fontFamily: MAIN_FONT,
                        color: "#8888AA",
                        fontSize: "20px",
                    }}
                >
                    {skillListError}
                </Typography>
            </Box>
        );
    }

    return (
        <>
            <RankingLeaderboard
                heading={`${HEADING} (${orgTypeLabel})`}
                subHeading={selectedSkill?.name_zh ?? ""}
                leaderboardData={leaderboardData}
                isLoading={isLoading || !selectedSkill}
                onSubHeadingClick={() => setDialogOpen(true)}
            />

            <SkillSelectDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                skillList={skillList}
                selectedSkill={selectedSkill}
                onSelect={handleSkillChange}
            />
        </>
    );
}
